import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
} from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  createExtensionMapping,
  fetchExtensionMapping,
  deleteExtensionMapping,
  IExtensionMappingDTO,
  ICommunicationServiceAccount,
} from 'wisestep-api';
import { ControlledTextInput, Label, WsButton, WsDataTable } from 'wisestep-base';

import RecruiterSuggestions from '../../../components/filters/components/RecruiterSuggestions';
import { useDialog } from '../../../hooks/useDialog';
import { warning, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

const ExtensionMappingListing = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openDialog, closeDialog] = useDialog();
  const [serviceAccount, setServiceAccount] = useState<ICommunicationServiceAccount>();

  const fetchAll = async () => {
    const resp = await fetchExtensionMapping();
    const serviceAccount = resp.data;
    setServiceAccount(serviceAccount);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const onSave = () => {
    fetchAll();
    closeDialog();
  };

  const tableData = useMemo(() => {
    if (serviceAccount?.phoneNumbers && serviceAccount.phoneNumbers.length > 0) {
      const data = serviceAccount.phoneNumbers.map((phoneNumber) => ({
        extension: phoneNumber.phoneNumber,
        usRecruiterEmail:
          phoneNumber.allocations?.find((allocation) => allocation.region === 'US')
            ?.recruiterEmail || '',
        ukRecruiterEmail:
          phoneNumber.allocations?.find((allocation) => allocation.region === 'UK')
            ?.recruiterEmail || '',
        apacRecruiterEmail:
          phoneNumber.allocations?.find((allocation) => allocation.region === 'APAC')
            ?.recruiterEmail || '',
      }));
      return data;
    } else return [];
  }, [serviceAccount]);

  const deleteMapping = async (phoneNumber: IExtensionMappingDTO) => {
    try {
      if (serviceAccount) {
        const resp = await deleteExtensionMapping(serviceAccount.id, phoneNumber);
        if (resp.data) {
          const serviceAccount = resp.data;
          setServiceAccount(serviceAccount);
          setNotification(success('PhoneNumber and extension mapping has been deleted.'));
        }
      }
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };

  const openExtMappingDialog = (defaultValues?) => {
    openDialog({
      children: (
        <ExtensionMappingForm
          onSave={onSave}
          onCancel={closeDialog}
          {...(defaultValues && { defaultValues })}
        />
      ),
    });
  };

  const columns = [
    {
      Header: 'Extension',
      accessor: 'extension',
    },
    {
      Header: 'US ID',
      accessor: 'usRecruiterEmail',
    },
    {
      Header: 'UK ID',
      accessor: 'ukRecruiterEmail',
    },
    {
      Header: 'APAC ID',
      accessor: 'apacRecruiterEmail',
    },
    {
      Header: '',
      accessor: 'edit',
      Cell: (props) => {
        const row = props.row.original;
        return (
          <IconButton onClick={() => deleteMapping(row)}>
            <DeleteIcon />
          </IconButton>
        );
      },
    },
  ];

  return (
    <>
      <Stack direction="row" p={2} justifyContent="flex-end" alignItems="center">
        <WsButton startIcon={<AddIcon />} variant="outlined" onClick={() => openExtMappingDialog()}>
          Add Extension
        </WsButton>
      </Stack>
      <WsDataTable dense columns={columns} data={tableData ?? []} />
    </>
  );
};

const ExtensionMappingForm = ({
  onSave,
  onCancel,
  defaultValues,
}: {
  onSave: () => void;
  onCancel: () => void;
  defaultValues?: any;
}) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const form = useForm({ defaultValues: { ...defaultValues } });

  const addAllocation = (recruiterEmail, region, output) => {
    output.allocations.push({
      recruiterEmail: recruiterEmail,
      region: region,
    });
  };

  const onSubmit = async (data: any) => {
    try {
      const output: IExtensionMappingDTO = {
        extension: data.extension,
        phoneNumber: '',
        allocations: [],
      };
      if (data.usRecruiterEmail) {
        const recruiterJson = JSON.parse(JSON.stringify(data.usRecruiterEmail));
        addAllocation(recruiterJson.email, 'US', output);
      }

      if (data.ukRecruiterEmail) {
        const recruiterJson = JSON.parse(JSON.stringify(data.ukRecruiterEmail));
        addAllocation(recruiterJson.email, 'UK', output);
      }

      if (data.apacRecruiterEmail) {
        const recruiterJson = JSON.parse(JSON.stringify(data.apacRecruiterEmail));
        addAllocation(recruiterJson.email, 'APAC', output);
      }
      const response = await createExtensionMapping(output);
      if (response.data) {
        onSave();
        setNotification(success('PhoneNumber and extension mapping has been added.'));
      }
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogTitle>Add/Edit Extension Mapping</DialogTitle>
        <DialogContent>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <ControlledTextInput
                name="extension"
                labelText="Extension"
                size="medium"
                required
                fullWidth={true}
              />
            </Grid>
            <Grid item xs={6}>
              <Label>US Recruiter</Label>
              <RecruiterSuggestions
                name="usRecruiterEmail"
                multiple={false}
                requireRecruiter={true}
                placeholder="Search Recruiter"
              />
            </Grid>
            <Grid item xs={6}>
              <Label>UK Recruiter</Label>
              <RecruiterSuggestions
                name="ukRecruiterEmail"
                multiple={false}
                requireRecruiter={true}
                placeholder="Search Recruiter"
              />
            </Grid>
            <Grid item xs={6}>
              <Label>APAC Recruiter</Label>
              <RecruiterSuggestions
                name="apacRecruiterEmail"
                multiple={false}
                requireRecruiter={true}
                placeholder="Search Recruiter"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="submit">Save</Button>
        </DialogActions>
      </form>
    </FormProvider>
  );
};

export default ExtensionMappingListing;
